import React, { Fragment, useEffect, useState } from "react";
import EADialog from "../EADialog";
import EATextField from "../EATextField";
import EAButtonBar from "../EAButtonBar";
import EAButton from "../EAButton";
import EADatePicker from "../EADatePicker";
import HistoryIcon from "@material-ui/icons/History";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import red from "@material-ui/core/colors/red";

const id = "auditInfoDialog";

const violationType = {
    missing: "MISSING",
    succeedingPresent: "SUCCEEDING_PRESENT",
    sameDatePresent: "SAME_DATE_PRESENT",
    inital: "INITIAL",
    stilllegen: "STILLLEGEN",
};

function AuditInfoDialog({ open, violation, onClose, onAuditInfoSet, onVerwerfen }) {
    const [step, setStep] = useState(1);
    const [grund, setGrund] = useState(null);
    const [gueltigAb, setGueltigAb] = useState(null);

    useEffect(() => {
        if (!violation) {
            setGrund(null);
            setGueltigAb(new Date());
            return;
        }
        switch (violation["auditInfoViolation"]) {
            case violationType.missing:
                setStep(1);
                setGrund(null);
                setGueltigAb(new Date());
                break;
            case violationType.inital:
                setStep(1);
                setGrund("Inbetriebnahme");
                setGueltigAb(new Date());
                break;
            case violationType.stilllegen:
                setStep(1);
                setGrund("Außerbetriebnahme");
                setGueltigAb(new Date());
                break;
            case violationType.succeedingPresent:
                setStep(2);
                break;
            case violationType.sameDatePresent:
                setStep(3);
                break;
            default:
                break;
        }

        if (violation.faultyAuditInfo) {
            setGrund(violation.faultyAuditInfo.aenderungsgrund);
            setGueltigAb(violation.faultyAuditInfo.gueltigAb);
        }
    }, [violation]);

    function getDateError() {
        if (!violation.auditInfoViolation) {
            return null;
        }
        if (violation.auditInfoViolation === violationType.succeedingPresent) {
            return "Eine Historisierung kann nicht rückwirkend, sondern immer nur aufbauend auf dem Enddatum des zeitlich letzten Eintrages erfolgen.";
        }
        return null;
    }

    function buildMessage() {
        switch (violation["auditInfoViolation"]) {
            case violationType.inital:
                return <p>Der initiale Eintrag in der Historie zur Inbetriebnahme muss aufgenommen werden.</p>;
            case violationType.stilllegen:
                return <p>Der letzte Eintrag in der Historie zur Stilllegung muss aufgenommen werden.</p>;
            case violationType.missing:
            case violationType.succeedingPresent:
            case violationType.sameDatePresent:
                return <p>Sollen die Änderungen in die Historie aufgenommen werden?</p>;
            default:
                return null;
        }
    }

    return (
        <EADialog
            open={open}
            closeFunction={onClose}
            titleChildren={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <HistoryIcon style={{ paddingRight: "var(--horizontal-spacing)", color: "var(--primary-dark)" }} />
                    <p>Autohistorisierung</p>
                </div>
            }
            content={
                <Fragment>
                    {step === 1 && (
                        <div style={{ margin: "var(--vertical-spacing) var(--horizontal-spacing)" }}>
                            <b>Sie haben historisch geführte Attribute verändert.</b>
                            {buildMessage()}
                        </div>
                    )}
                    {step === 2 && (
                        <div>
                            <p style={{ marginBottom: "var(--vertical-spacing)" }}>
                                Bitte geben Sie hierzu das Datum an, seit dem die Änderung gilt.
                            </p>
                            <EADatePicker
                                error={getDateError()}
                                label={"Gültig ab"}
                                id={id + "-gueltig-ab"}
                                value={gueltigAb}
                                onChange={(newValue) => setGueltigAb(newValue)}
                                disableFuture={true}
                            />

                            <p style={{ marginTop: "calc(var(--vertical-spacing)*2)", marginBottom: "var(--vertical-spacing)" }}>
                                Zusätzlich können Sie über eine Bemerkung den Grund der Historisierung näher beschrieben.
                            </p>
                            <EATextField
                                value={grund}
                                onChange={(newValue) => setGrund(newValue.target.value)}
                                label={"Bemerkung"}
                                multiline
                                rows={5}
                                id={id + "-aenderungsgrund"}
                                className={"w-100"}
                            />
                        </div>
                    )}
                    {step === 3 && (
                        <div style={{ margin: "var(--vertical-spacing) var(--horizontal-spacing)" }}>
                            <b>Für das angegebene Datum besteht bereits eine Historie.</b>
                            <p>Sollen die vorhandene Historie durch den neu zu historisierenden Datensatz überschrieben werden?</p>
                        </div>
                    )}
                </Fragment>
            }
            buttons={
                (step === 1 && (
                    <EAButtonBar style={{ width: "800px" }}>
                        <EAButton
                            id={id + "-verwerfen"}
                            label={"Änderungen verwerfen"}
                            onClick={onVerwerfen}
                            variant="text"
                            style={{ color: red[500] }}
                            startIcon={<DeleteForeverIcon color={"error"} />}
                        />
                        {violation?.auditInfoViolation !== violationType.inital &&
                            violation?.auditInfoViolation !== violationType.stilllegen && (
                                <EAButton
                                    id={id + "-skip"}
                                    label={"Historie nicht anlegen"}
                                    onClick={() => {
                                        onAuditInfoSet({ significant: false });
                                        onClose();
                                    }}
                                />
                            )}
                        <EAButton
                            id={id + "-neue-version"}
                            label={"Änderungen in die Historie aufnehmen"}
                            onClick={() => setStep(2)}
                            color="primary"
                            startIcon=<HistoryIcon style={{ filter: "brightness(0) invert(1)" }} />
                        />
                    </EAButtonBar>
                )) ||
                ((step === 2 || step === 3) && (
                    <EAButtonBar style={{ width: "800px" }}>
                        <EAButton
                            id={id + "-zurueck"}
                            label={"Zurück"}
                            onClick={() => {
                                const newStep = step - 1;
                                setStep(newStep);
                                if (newStep === 1) {
                                    setGrund(null);
                                    setGueltigAb(new Date());
                                }
                            }}
                        />
                        <EAButton
                            id={id + "-anlegen"}
                            label={step !== 3 ? "Historie anlegen" : "Historie überschreiben"}
                            onClick={() => {
                                const auditInfo = {};
                                auditInfo.aenderungsgrund = grund;
                                auditInfo.gueltigAb = gueltigAb;
                                auditInfo.significant = true;
                                if (step === 3) {
                                    auditInfo.override = true;
                                }
                                onAuditInfoSet(auditInfo);
                                onClose();
                            }}
                            color="primary"
                            startIcon=<HistoryIcon style={{ filter: "brightness(0) invert(1)" }} />
                        />
                    </EAButtonBar>
                ))
            }
        />
    );
}

export default AuditInfoDialog;
